import { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Navigation } from '../components/Navigation';
import { HeroSection } from '../components/sections/HeroSection';
import { CareerSection } from '../components/sections/CareerSection';
import { WritingsSection } from '../components/sections/WritingsSection';
import { RecommendationsSection } from '../components/sections/RecommendationsSection';
import { ExperimentsSection } from '../components/sections/ExperimentsSection';
import { ResearchSection } from '../components/sections/ResearchSection';
import { Footer } from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Landing() {
    const [writings, setWritings] = useState([]);
    const [experiments, setExperiments] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [research, setResearch] = useState([]);
    const [career, setCareer] = useState([]);
    const [siteSettings, setSiteSettings] = useState({
        writings_empty_message: "I fed all of my writing to my AI Agent. It's now out in the wild trying to take over the world. BRB.",
        recommendations_empty_message: "Patience. I'm watching YouTube for your next recco :P",
        experiments_empty_message: "An evil scientist stole the experiments. Please stay tuned.",
        research_empty_message: "Research in progress. The lab is under construction.",
        flip_card_hint: "flip to reveal your new fav character"
    });
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        try {
            const [writingsRes, experimentsRes, recommendationsRes, researchRes, careerRes, settingsRes] = await Promise.all([
                axios.get(`${API}/writings?published_only=true`),
                axios.get(`${API}/experiments?published_only=true`),
                axios.get(`${API}/recommendations?published_only=true&exclude_expired=true`),
                axios.get(`${API}/research?published_only=true`),
                axios.get(`${API}/career`),
                axios.get(`${API}/settings`)
            ]);

            setWritings(writingsRes.data);
            setExperiments(experimentsRes.data);
            setRecommendations(recommendationsRes.data);
            setResearch(researchRes.data);
            setCareer(careerRes.data);
            setSiteSettings(settingsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        // Track visitor
        axios.post(`${API}/track?page=/`).catch(() => {});
        
        fetchData();
    }, [fetchData]);

    // Build JSON-LD structured data
    const structuredData = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Person",
                "@id": "https://shreyansahukar.com/#person",
                "name": "Shreyan Sahukar",
                "url": "https://shreyansahukar.com",
                "sameAs": ["https://www.linkedin.com/in/shreyansahukar"],
                "jobTitle": career[0]?.title || "Builder",
                "worksFor": career[0]?.company ? {
                    "@type": "Organization",
                    "name": career[0].company
                } : undefined
            },
            {
                "@type": "WebSite",
                "@id": "https://shreyansahukar.com/#website",
                "url": "https://shreyansahukar.com",
                "name": "Shreyan Sahukar",
                "description": "Personal brand page showcasing professional journey, writings, recommendations, and experiments.",
                "author": { "@id": "https://shreyansahukar.com/#person" }
            }
        ]
    };

    return (
        <div className="min-h-screen bg-background" data-testid="landing-page">
            <Helmet>
                <title>Shreyan Sahukar | Glasshouse Resident. Stone Thrower.</title>
                <meta name="description" content="Personal brand page showcasing professional journey, writings, recommendations, and experiments in technology and innovation." />
                <meta property="og:title" content="Shreyan Sahukar | Glasshouse Resident. Stone Thrower." />
                <meta property="og:description" content="Personal brand page showcasing professional journey, writings, recommendations, and experiments in technology and innovation." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://shreyansahukar.com" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Shreyan Sahukar | Glasshouse Resident. Stone Thrower." />
                <link rel="canonical" href="https://shreyansahukar.com" />
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>
            <Navigation />
            
            <main>
                <HeroSection flipCardHint={siteSettings.flip_card_hint} />
                
                <CareerSection career={career} loading={loading} />
                
                {/* Full width Writings section */}
                <WritingsSection 
                    writings={writings} 
                    loading={loading} 
                    emptyMessage={siteSettings.writings_empty_message}
                />
                
                {/* Half-width panels for Recs and Experiments */}
                <section className="py-16 sm:py-24 bg-muted/30" data-testid="content-panels">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <RecommendationsSection 
                                recommendations={recommendations} 
                                loading={loading}
                                emptyMessage={siteSettings.recommendations_empty_message}
                            />
                            <ExperimentsSection 
                                experiments={experiments} 
                                loading={loading}
                                emptyMessage={siteSettings.experiments_empty_message}
                            />
                        </div>
                    </div>
                </section>

                {/* Research section - gated access */}
                <ResearchSection 
                    research={research}
                    loading={loading}
                    emptyMessage={siteSettings.research_empty_message}
                />
            </main>

            <Footer />
        </div>
    );
}
